import {sendCustomMetric} from '@github-ui/stats'

import {BoardMoveRefusedError, ViewRankStaleError} from './board-move-errors'

/** Which writes a drop has sent so far. */
export type SentBoardWrites = {field: boolean; rank: boolean}

/**
 * Runs the writes for one drop and records how long they took, from the first request to the
 * last answer, as `issues.board.move.duration`. Records nothing when no write went out.
 *
 * Tagged `kind` (`rank`, `field` or `field_and_rank`, by the writes sent) and `outcome` (`saved`,
 * `stale`, `refused` or `failed`). A rank that fails after its field write stood is the outcome.
 */
export async function timeBoardMove<T extends {rankError?: unknown}>(
  write: (sent: SentBoardWrites) => Promise<T>,
): Promise<T> {
  const sent: SentBoardWrites = {field: false, rank: false}
  const startedAt = performance.now()
  try {
    const result = await write(sent)
    report(startedAt, sent, result.rankError)
    return result
  } catch (error) {
    report(startedAt, sent, error)
    throw error
  }
}

function report(startedAt: number, sent: SentBoardWrites, error: unknown) {
  if (!sent.field && !sent.rank) return

  try {
    sendCustomMetric({
      name: 'ISSUES_BOARD_MOVE_DURATION',
      value: performance.now() - startedAt,
      tags: {kind: kindOf(sent), outcome: outcomeOf(error)},
    })
  } catch (metricError) {
    // Rethrown on a timer, so it reaches telemetry without changing whether the drop saved.
    setTimeout(() => {
      throw metricError
    })
  }
}

function kindOf({field, rank}: SentBoardWrites) {
  if (field && rank) return 'field_and_rank'
  return field ? 'field' : 'rank'
}

function outcomeOf(error: unknown) {
  if (error === undefined) return 'saved'
  if (error instanceof ViewRankStaleError) return 'stale'
  if (error instanceof BoardMoveRefusedError) return 'refused'
  return 'failed'
}
